import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowsClockwise, CheckCircle, LinkBreak, Warning, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const formatDate = (value) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

const formatRange = (range) => {
  if (!range?.start && !range?.end) return "No dated transactions";
  return `${formatDate(range?.start)} - ${formatDate(range?.end)}`;
};

const institutionFor = (item) => item?.institution_name || item?.institution || "";
const idFor = (item) => item?.id || item?.item_id;

export const UnlinkIntentDialog = ({
  open,
  onOpenChange,
  item,
  hid,
  allowErase = false,
  onDisconnect,
  onErased,
}) => {
  const [step, setStep] = useState("intent");
  const [preview, setPreview] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [busy, setBusy] = useState(false);

  const itemId = idFor(item);
  const fallbackInstitution = institutionFor(item);
  const previewInstitution = preview?.institution || fallbackInstitution;
  const typedMatches = useMemo(
    () => confirmName.trim().toLowerCase() === previewInstitution.trim().toLowerCase(),
    [confirmName, previewInstitution]
  );

  useEffect(() => {
    if (!open) {
      setStep("intent");
      setPreview(null);
      setReceipt(null);
      setConfirmName("");
      setBusy(false);
    }
  }, [open]);

  const close = () => onOpenChange?.(false);

  const disconnectOnly = async () => {
    setBusy(true);
    try {
      await onDisconnect?.(item);
      close();
    } finally {
      setBusy(false);
    }
  };

  const chooseErase = async () => {
    if (!allowErase || !itemId) return;
    setBusy(true);
    setStep("erase");
    try {
      const r = await api.get(`/plaid/items/${itemId}/erase-preview?household_id=${hid}`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load erase preview");
      setStep("intent");
    } finally {
      setBusy(false);
    }
  };

  const confirmErase = async () => {
    if (!typedMatches || !itemId) return;
    setBusy(true);
    try {
      const r = await api.post(`/plaid/items/${itemId}/erase?household_id=${hid}`, {
        confirm_institution_name: confirmName.trim(),
      });
      setReceipt(r.data);
      setStep("receipt");
      await onErased?.(item, r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not erase this connection");
    } finally {
      setBusy(false);
    }
  };

  const renderIntent = () => (
    <>
      <DialogHeader>
        <DialogTitle>Disconnect {fallbackInstitution || "this bank"}?</DialogTitle>
        <DialogDescription>
          Choose what happens to this bank connection and the history it brought into OurHomebase.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3">
        <button
          type="button"
          data-testid="unlink-disconnect-only"
          onClick={disconnectOnly}
          disabled={busy}
          className="w-full rounded-sm border-2 border-primary bg-primary/5 p-4 text-left transition hover:bg-primary/10 disabled:pointer-events-none disabled:opacity-50"
        >
          <div className="flex items-start gap-3">
            <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-sm bg-primary text-primary-foreground">
              {busy ? <ArrowsClockwise size={17} className="animate-spin" /> : <LinkBreak size={17} weight="bold" />}
            </span>
            <span>
              <span className="block text-sm font-bold">Disconnect only (recommended)</span>
              <span className="mt-1 block text-sm text-muted-foreground">
                We stop syncing and revoke access at your bank. Your existing transactions and history stay in OurHomebase - your tax records are unaffected. You can reconnect anytime.
              </span>
            </span>
          </div>
        </button>

        {allowErase && (
          <button
            type="button"
            data-testid="unlink-disconnect-erase"
            onClick={chooseErase}
            disabled={busy}
            className="w-full rounded-sm border border-destructive/40 p-4 text-left transition hover:bg-destructive/10 disabled:pointer-events-none disabled:opacity-50"
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-sm bg-destructive/10 text-destructive">
                <Trash size={17} weight="bold" />
              </span>
              <span>
                <span className="block text-sm font-bold text-destructive">Disconnect and erase</span>
                <span className="mt-1 block text-sm text-muted-foreground">
                  We stop syncing AND permanently remove the data this connection brought in.
                </span>
              </span>
            </div>
          </button>
        )}
      </div>

      <DialogFooter>
        <Button data-testid="unlink-cancel" variant="outline" onClick={close} disabled={busy}>
          Cancel
        </Button>
      </DialogFooter>
    </>
  );

  const renderErase = () => (
    <>
      <DialogHeader>
        <DialogTitle>Erase data from {previewInstitution || "this connection"}</DialogTitle>
        <DialogDescription>
          Review the blast radius before permanently erasing this connection's imported history.
        </DialogDescription>
      </DialogHeader>

      {!preview ? (
        <div className="flex items-center gap-2 rounded-sm border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
          <ArrowsClockwise size={16} className="animate-spin" />
          Loading erase preview
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 rounded-sm border border-border bg-card p-4 text-sm sm:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Institution</p>
              <p className="font-semibold">{previewInstitution}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Date range</p>
              <p className="font-semibold">{formatRange(preview.date_range)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Transactions</p>
              <p className="font-semibold">{preview.apu_count ?? 0}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Archived documents</p>
              <p className="font-semibold">{preview.document_count ?? 0}</p>
            </div>
          </div>

          {preview.accounts?.length > 0 && (
            <div className="rounded-sm border border-border p-3">
              <p className="text-xs font-semibold uppercase text-muted-foreground">Accounts</p>
              <p className="mt-1 text-sm font-medium">{preview.accounts.join(", ")}</p>
            </div>
          )}

          {(preview.confirmed_reconciliation_refs ?? 0) > 0 && (
            <div
              data-testid="erase-blast-radius-warning"
              className="flex gap-3 rounded-sm border border-warning/50 bg-warning/10 p-4 text-sm"
            >
              <Warning size={18} weight="bold" className="mt-0.5 shrink-0 text-warning" />
              <p>
                {preview.confirmed_reconciliation_refs} confirmed reconciliation decisions reference this data. Erasing
                removes the source records those decisions were based on.
              </p>
            </div>
          )}

          <div>
            <label className="text-sm font-semibold" htmlFor="erase-confirm-input">
              Type {previewInstitution} to erase this data
            </label>
            <Input
              id="erase-confirm-input"
              data-testid="erase-confirm-input"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              className="mt-2 rounded-sm"
              autoComplete="off"
            />
          </div>
        </div>
      )}

      <DialogFooter>
        <Button data-testid="erase-cancel" variant="outline" onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button
          data-testid="erase-confirm-button"
          variant="destructive"
          onClick={confirmErase}
          disabled={!preview || !typedMatches || busy}
        >
          {busy ? <ArrowsClockwise size={15} className="animate-spin" /> : <Trash size={15} weight="bold" />}
          Erase permanently
        </Button>
      </DialogFooter>
    </>
  );

  const renderReceipt = () => (
    <>
      <DialogHeader>
        <DialogTitle>Erasure receipt</DialogTitle>
        <DialogDescription>
          This receipt records who chose to erase the connection and what was removed.
        </DialogDescription>
      </DialogHeader>

      <div data-testid="erase-receipt" className="space-y-4">
        <div className="flex items-start gap-3 rounded-sm border border-success/40 bg-success/10 p-4">
          <CheckCircle size={20} weight="bold" className="mt-0.5 shrink-0 text-success" />
          <div>
            <p className="font-semibold">{receipt?.institution || previewInstitution} was erased</p>
            <p className="text-sm text-muted-foreground">
              Decided by {receipt?.decided_by || "Unknown"} on {formatDate(receipt?.erased_at)}.
            </p>
          </div>
        </div>

        <div className="grid gap-3 rounded-sm border border-border p-4 text-sm sm:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground">Transactions erased</p>
            <p className="font-semibold">{receipt?.counts?.apus ?? 0}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground">Archives erased</p>
            <p className="font-semibold">{receipt?.counts?.documents ?? 0}</p>
          </div>
        </div>

        {receipt?.backup_note && (
          <p className="rounded-sm border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            {receipt.backup_note}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button onClick={close}>Close</Button>
      </DialogFooter>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="unlink-intent-dialog" className="max-w-xl rounded-sm">
        {step === "receipt" ? renderReceipt() : step === "erase" ? renderErase() : renderIntent()}
      </DialogContent>
    </Dialog>
  );
};
